import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Award, CheckCircle, ArrowRight, Phone, Mail, MessageCircle,
  AlertTriangle, FileText, Clock, MapPin, Briefcase, Building2,
  ChevronRight, XCircle, FileCheck, BadgeCheck, Stethoscope,
  HardHat, Wrench, Car, GraduationCap, Globe2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta, useFAQSchema } from '@/hooks/useSEO';
import { useContactChannel } from '@/hooks/useContactChannel';

/**
 * /saudi-arabia/work-visa  (premium upgrade, Feb 2026)
 * Replaces the legacy CMS-driven SaudiVisaPage rendering for this URL.
 * Mirrors the SaudiTouristVisaPage / SaudiBusinessVisitVisaPage layout
 * pattern with role-specific (Engineer/Technician/Nurse/Driver/Labour) data
 * tables and a deep QVP (Qualification Verification Program) walkthrough.
 */

const MEA_REG = 'B-0762/DEL/PER/1000+/5/9148/2015';

const ZAFCO_ADVANTAGES = [
  { label: 'Direct Embassy Empanelment', detail: 'No intermediaries — ZAFCO submits directly to the Royal Saudi Embassy.' },
  { label: 'Pan-India Operational Network', detail: 'Dedicated centres in Delhi, Mumbai, Hyderabad, Chennai, Kolkata.' },
  { label: 'Qiwa & MHRSD Platform Mastery', detail: 'Real-time contract verification and Electronic Wakala generation.' },
  { label: 'GAMCA / Wafid Medical Coordination', detail: '100% appointment booking success at certified medical centres.' },
  { label: 'Degree Attestation Precision', detail: 'State HRD → MEA → Saudi Embassy end-to-end handling.' },
  { label: 'QVP Verification Expertise', detail: 'Complete Qualification Verification Program processing for Technician visas.' },
  { label: '24/7 Expert Support', detail: 'Personalised guidance from application to visa stamping.' },
];

const VISA_TYPES = [
  {
    icon: GraduationCap,
    badge: 'High-Skill',
    badgeTone: 'bg-emerald-100 text-emerald-800',
    title: '1. Saudi Professional Visa & Saudi Engineer Visa',
    intro: 'Highly qualified engineers, architects, project managers, and technical specialists holding Bachelor\u2019s or Master\u2019s degrees in engineering disciplines.',
    rows: [
      ['Educational Qualification', 'B.Tech / M.Tech / BE / ME from AICTE / UGC-recognised university'],
      ['Professional Registration', 'Mandatory IEI (Institution of Engineers India) membership certificate'],
      ['Degree Attestation', 'State HRD → MEA Apostille → Saudi Embassy Attestation'],
      ['Experience Proof', 'Minimum 5 years with employment letters, salary certificates, relieving letters'],
      ['Contract Type', 'Qiwa Employment Contract — minimum SAR 8,000 monthly salary threshold'],
      ['Medical Requirements', 'GAMCA / Wafid Medical for Saudi Visa — full panel (chest X-ray, HIV, Hep B/C)'],
    ],
    docs: [
      'Original degree certificate + mark sheets (all years)',
      'Engineering Council (IEI) membership certificate',
      '5 years of experience letters with company seals',
      'Qiwa employment contract (Electronic Wakala)',
      'Passport (valid 6+ months)',
      'GAMCA / Wafid medical clearance report',
      'Passport-size photographs (2 copies)',
    ],
    note: 'DataFlow professional verification or QVP is NOT required for the Saudi Engineer Visa. Only Engineering Council (IEI) membership certificate is mandatory.',
    noteTone: 'amber',
  },
  {
    icon: Stethoscope,
    badge: 'QVP / DataFlow MANDATORY',
    badgeTone: 'bg-red-100 text-red-800',
    title: '2. Saudi Technician Visa (QVP MANDATORY) & Saudi Nurse Visa (DataFlow MANDATORY)',
    intro: 'Saudi Technician Visa covers certified diploma holders and skilled tradesmen — QVP is MANDATORY in 2026. Saudi Nurse Visa requires DataFlow Professional Verification.',
    isComparison: true,
    headers: ['Requirement', 'Saudi Technician Visa', 'Saudi Nurse Visa'],
    rows: [
      ['Educational Qualification', '3-year diploma (ITI / Polytechnic / State Technical Board)', 'B.Sc. Nursing / P.G. Diploma in Nursing'],
      ['Professional Registration', 'State Technical Education Board certification', 'Indian Nursing Council (INC) + State Nursing Council license'],
      ['Qualification Verification', 'QVP — MANDATORY for ALL Technician visas', 'DataFlow Professional Verification (degree + INC)'],
      ['Experience Proof', 'Minimum 3 years technical experience', 'Minimum 2 years clinical nursing experience'],
      ['Contract Type', 'Qiwa Labour Visa contract (SAR 4,500–6,000)', 'Qiwa Employment Contract (SAR 6,000–8,000)'],
      ['Medical Requirements', 'Wafid — standard panel', 'GAMCA / Wafid — full healthcare panel'],
      ['Additional Documents', 'Trade certification, QVP clearance report, skill test report', 'INC registration, BLS / ACLS certification, DataFlow report'],
    ],
  },
  {
    icon: Car,
    badge: 'Entry-Level / Trade',
    badgeTone: 'bg-slate-200 text-slate-800',
    title: '3. Saudi Driver Visa & Saudi Labour Visa (Saudi Labor Visa from India)',
    intro: 'The Saudi Driver Visa is for licensed drivers (light/heavy vehicles) with valid Indian DL and RTO certification. The Saudi Labour Visa covers entry-level construction workers, factory operators, and general labour.',
    isComparison: true,
    headers: ['Requirement', 'Saudi Driver Visa', 'Saudi Labour / Labor Visa'],
    rows: [
      ['Educational Qualification', '10th pass + Valid Indian Driving Licence', '8th pass (basic literacy)'],
      ['Professional Certification', 'RTO Driving Licence (valid 2+ years)', 'Trade certification (if applicable)'],
      ['Experience Proof', '3 years driving experience with employer letters', 'Not mandatory (fresh workers accepted)'],
      ['Contract Type', 'Qiwa Labour Visa (SAR 3,500–4,500)', 'Qiwa Labour Visa (SAR 3,000–4,000)'],
      ['Medical Requirements', 'Wafid — standard panel', 'Wafid — standard panel'],
      ['Special Documents', 'Driving licence copy, RTO verification report, NOC', 'Basic employment contract, sponsor invitation letter'],
      ['Verification Required', 'Driving licence attestation (State + MEA)', 'None (no degree required)'],
      ['QVP Required?', 'No', 'No'],
    ],
    note: 'Drivers must possess a valid Indian Driving Licence (minimum 2 years validity) with RTO verification report. Heavy-vehicle drivers (trucks, buses) require additional Commercial Driving Licence certification. Saudi Work Visa Delhi centres verify driving licence authenticity through RTO databases before Embassy submission.',
    noteTone: 'blue',
  },
];

const QVP_REQUIRED = [
  'Electrician Technician', 'Welder Technician', 'Mechanical Technician', 'HVAC Technician',
  'Industrial Machinery Technician', 'Automotive Technician', 'Electronics Technician',
  'Plumbing Technician', 'Civil Construction Technician', 'Electronics & Communication Technician',
];

const QVP_NOT_REQUIRED = [
  'Saudi Engineer Visa (requires IEI Engineering Council only)',
  'Saudi Nurse Visa (requires DataFlow Professional Verification)',
  'Saudi Professional Visa (requires degree attestation only)',
  'Saudi Labour Visa (no degree required)',
  'Saudi Driver Visa (requires driving licence only)',
];

const QVP_STEPS = [
  {
    title: 'Step 1 — Employer Initiates QVP on Qiwa Platform',
    points: [
      'Saudi employer logs into qiwa.mhrsd.gov.sa',
      'Selects "QVP Verification" service under the Technician visa category',
      'Enters candidate\u2019s diploma details: certificate number, issuing institution, year, program duration (must be 3+ years), specialisation',
      'Uploads scanned original diploma + mark sheets (all years)',
    ],
  },
  {
    title: 'Step 2 — Verification Request Sent to Indian Institution',
    points: [
      'Qiwa auto-routes verification to the issuing Indian institution',
      'Institution receives email / SMS with QVP reference number',
      'Institution must verify within 7–10 business days: authenticity, enrolment, duration, marks, AICTE / State Board recognition',
    ],
  },
  {
    title: 'Step 3 — Institution Submits QVP Verification Report',
    points: [
      'Institution uploads QVP Verification Report directly to Qiwa',
      '✅ Verified — diploma authentic, program matches requirements',
      '❌ Not Verified — fraudulent, insufficient duration, or institution not recognised',
      '⚠️ Partial Verification — discrepancies require clarification',
    ],
  },
  {
    title: 'Step 4 — Candidate Receives QVP Clearance Report',
    points: [
      'If verified: candidate receives QVP Clearance Report (digital PDF) via Qiwa portal',
      'Report contains: QVP reference number, candidate details, diploma details, status "VERIFIED — AUTHENTIC", program duration, specialisation, verification date, Qiwa QR code for authenticity validation',
    ],
  },
  {
    title: 'Step 5 — Submit QVP Clearance Report for Visa Stamping',
    points: [
      'Candidate downloads the QVP Clearance Report from Qiwa',
      'Sends report to ZAFCO via email or the ZAFCO portal',
      'ZAFCO re-verifies authenticity on Qiwa and includes the report in the Saudi Work Visa application package for Embassy submission',
    ],
  },
];

const QVP_REJECTIONS = [
  ['Fraudulent Diploma', '❌ Permanent Rejection', 'No appeal — visa rejected'],
  ['Institution Not Recognised (not AICTE / State Board approved)', '❌ Permanent Rejection', 'No appeal — must obtain recognised diploma'],
  ['Program Duration < 3 Years (e.g., 1-year certificate)', '❌ Permanent Rejection', 'No appeal — must complete 3-year diploma'],
  ['Certificate Number Mismatch (institution records don\u2019t match)', '⚠️ Temporary Rejection', 'Institution can clarify + re-submit (7 days)'],
  ['Marks Discrepancy', '⚠️ Temporary Rejection', 'Institution can correct records + re-verify (10 days)'],
  ['Institution Not Responding (no verification within 10 days)', '⚠️ Temporary Rejection', 'Employer can contact institution + resubmit (14 days)'],
  ['Specialisation Not Aligned with job role', '⚠️ Temporary Rejection', 'Employer can change job role or candidate can re-qualify'],
];

const QVP_TIMELINE = [
  ['Employer initiates QVP on Qiwa', '1 day', 'INR 500–700 (Qiwa service fee)'],
  ['Institution verification', '7–10 business days', 'INR 200–500'],
  ['QVP Clearance Report generation', '1–2 days', 'Free (included in Qiwa)'],
  ['TOTAL QVP processing time', '9–13 business days (2–3 weeks)', 'INR 700–1,200 total'],
];

const QIWA_STEPS = [
  'Employer registers on Qiwa: Saudi company obtains a Qiwa account via MHRSD registration.',
  'Job offer creation: employer creates the job offer with role, salary, duration, and benefits.',
  'Worker details input: candidate\u2019s passport number, name, DOB, nationality.',
  'Contract generation: Qiwa generates the Saudi Employment Contract Visa (electronic version).',
  'Electronic Wakala (Power of Attorney): auto-generated — legal authorisation for visa processing.',
  'Contract approval: MHRSD approves the contract (1–2 business days).',
  'Work Permit issuance: Qiwa issues a Saudi Work Permit Visa with a unique permit number.',
];

const QIWA_COMPLIANCE = [
  'Salary threshold: Professional ≥ SAR 8,000 · Technician ≥ SAR 4,500 · Labour ≥ SAR 3,000',
  'Contract validity: minimum 2 years (renewable)',
  'Benefits: housing, transportation, medical insurance, annual flight tickets — mandatory',
  'Job role alignment: contract role must match candidate\u2019s degree / certification',
];

const MUQEEM_STEPS = [
  'Employer submits the Electronic Wakala to Muqeem',
  'MOFA validates the Wakala against the Qiwa contract',
  'Muqeem generates the Saudi Work Permit Visa number (12-digit unique ID)',
  'Candidate receives the Saudi Work Visa application reference number via email',
];

const MEDICAL_COMPARISON = [
  ['Platform', 'gateway.gamca.org.sa', 'www.wafid.com'],
  ['Appointment booking', 'Manual (email / phone)', 'Online self-service'],
  ['Report delivery', 'Physical + email', 'Digital (instant download)'],
  ['Processing time', '3–5 business days', '1–2 business days'],
  ['Validity', '3 months from test date', '3 months from test date'],
  ['Approved centres in India', '45 GAMCA centres', '50+ Wafid centres'],
];

const MEDICAL_STEPS = [
  {
    title: 'Step 1 — Book Wafid / GAMCA Appointment Online',
    points: [
      'Visit www.wafid.com (or gateway.gamca.org.sa for GAMCA)',
      'Register with passport number, email, phone',
      'Select the nearest certified medical centre (Delhi, Mumbai, Hyderabad, Chennai, Kolkata)',
      'Choose appointment date — usually 1–3 days availability',
      'Pay medical fee online (SAR 450–600 ≈ INR 10,000–14,000)',
    ],
  },
  {
    title: 'Step 2 — Visit Medical Centre with Required Documents',
    points: [
      'Original passport (valid 6+ months)',
      'Passport-size photograph (white background)',
      'Saudi Work Permit Visa number (from Muqeem)',
      'Appointment confirmation slip',
      'Visa fee payment receipt',
    ],
  },
  {
    title: 'Step 3 — Complete the Medical Test',
    points: [
      'Blood test: HIV, Hepatitis B, Hepatitis C, Syphilis (VDRL)',
      'Chest X-ray: Tuberculosis (TB) screening',
      'Urine test: drug screening, kidney function',
      'Physical examination: height, weight, blood pressure, vision',
      'Healthcare professionals: extra infectious disease panel + dental examination + complete medical report',
    ],
  },
  {
    title: 'Step 4 — Receive Medical Clearance Report',
    points: [
      'Pass: "Medical Clearance Approved" — digital report from the Wafid portal',
      'Fail: "Medical Rejection" — specific condition listed (TB, HIV, Hepatitis, etc.)',
      'Validity: 3 months from test date (must complete stamping within this window)',
    ],
  },
  {
    title: 'Step 5 — Submit Medical Report to ZAFCO for Visa Stamping',
    points: [
      'Download the Wafid Medical for Saudi Visa report (PDF)',
      'Send to ZAFCO via email or upload to the ZAFCO portal',
      'ZAFCO verifies authenticity and includes it in the Saudi Work Visa application package',
    ],
  },
];

const MEDICAL_REJECTIONS = [
  ['HIV Positive', '❌ Permanent Rejection', 'No appeal'],
  ['Hepatitis B / C Positive', '❌ Permanent Rejection', 'No appeal'],
  ['Active Tuberculosis (TB)', '❌ Temporary Rejection', '6-month treatment + re-test'],
  ['Syphilis (VDRL Positive)', '❌ Temporary Rejection', 'Treatment + re-test (1 month)'],
  ['Drug Positive (Urine)', '❌ Temporary Rejection', 'Detox + re-test (2 weeks)'],
  ['High Blood Pressure', '⚠️ Conditional', 'Medication + re-test'],
  ['Vision Deficiency', '⚠️ Conditional', 'Corrective glasses allowed'],
];

const PCC_TRIGGERS = [
  'If the Saudi employer explicitly requests PCC in the Qiwa contract',
  'If the candidate has a previous criminal record in any country',
  'If the candidate is applying for government-sector employment in Saudi Arabia',
  'If MHRSD / MOFA flags the candidate for additional verification',
];

const PCC_STEPS = [
  {
    title: 'Step 1 — Apply Online at Passport Seva Portal',
    points: [
      'Visit passportindia.gov.in',
      'Register / sign in, select "Apply for Police Clearance Certificate"',
      'Fill the form: personal details, current address, last 5 years\u2019 addresses, purpose ("Saudi Work Visa Employment"), Saudi employer details',
    ],
  },
  {
    title: 'Step 2 — Schedule Appointment at PSK / RPO',
    points: [
      'Select nearest Passport Seva Kendra (PSK) or Regional Passport Office (RPO)',
      'Choose appointment date (1–2 weeks availability)',
      'Pay PCC fee online — INR 500',
    ],
  },
  {
    title: 'Step 3 — Visit PSK / RPO with Required Documents',
    points: [
      'Original passport + copy (all pages)',
      'Address proof: Aadhaar, Voter ID, electricity bill, rent agreement',
      'Previous address proof (if changed in last 5 years)',
      'Saudi Work Permit Visa copy (from Muqeem)',
      'Employment contract copy (from Qiwa)',
      'Passport-size photographs (2 copies)',
    ],
  },
  {
    title: 'Step 4 — Police Verification (15–30 days)',
    points: [
      'Local police station visits candidate\u2019s residence',
      'Verifies address, criminal record, character',
      'Submits report to RPO',
    ],
  },
  {
    title: 'Step 5 — Receive Police Clearance Certificate',
    points: [
      'Valid PCC: "No Criminal Record Found" — physical certificate',
      'Rejected PCC: "Criminal Record Found" — visa application rejected',
      'Validity: 6 months from issuance date',
    ],
  },
];

const ATTESTATION_LIFECYCLE = [
  {
    tier: 'Tier 1 — State HRD Attestation',
    duration: '7–15 days',
    steps: [
      'Identify the issuing state (e.g., Delhi University → Delhi HRD; ITI / Diploma → State Technical Education Board)',
      'Submit original degree + mark sheets, Aadhaar / Voter ID, passport copy, Saudi Work Permit Visa copy',
      'State HRD verifies with university records, checks AICTE / UGC recognition, issues HRD Attestation Certificate (stamped on degree)',
    ],
  },
  {
    tier: 'Tier 2 — MEA Apostille',
    duration: '7–15 days',
    steps: [
      'Submit HRD-attested degree, HRD attestation certificate, passport copy, MEA application form',
      'MEA verifies the HRD attestation and issues an MEA Apostille (stamped on back of degree)',
      'Apostille is valid for all 140+ Hague Convention countries',
    ],
  },
  {
    tier: 'Tier 3 — Saudi Embassy Attestation',
    duration: '10–20 days',
    steps: [
      'Submit MEA-apostilled degree, Saudi Work Permit Visa copy, Electronic Wakala (from Qiwa), attestation application form, attestation fee',
      'Saudi Embassy verifies MEA apostille, cross-checks with the Indian university',
      'Issues Saudi Embassy Attestation (stamp + seal). Final document carries 3 stamps: HRD + MEA + Embassy',
    ],
  },
];

const ATTESTATION_TIMELINE = [
  ['State HRD Attestation', '7–15 days', 'INR 1,000–2,000 per document'],
  ['MEA Apostille', '7–15 days', 'INR 500 per document'],
  ['Saudi Embassy Attestation', '10–20 days', 'INR 2,000–4,000 per document'],
  ['TOTAL', '24–50 days', 'INR 3,500–6,500 per document'],
];

const ATTESTATION_DOCS_BY_CATEGORY = [
  ['Saudi Professional Visa', 'Bachelor\u2019s / Master\u2019s degree + mark sheets', 'No (attestation only)'],
  ['Saudi Engineer Visa', 'Engineering degree + IEI certificate', 'No (IEI membership only)'],
  ['Saudi Technician Visa', 'Diploma certificate + ITI trade certificate', 'Yes — QVP (MANDATORY)'],
  ['Saudi Nurse Visa', 'B.Sc. Nursing + INC registration', 'Yes — DataFlow (MANDATORY)'],
  ['Saudi Driver Visa', 'Driving licence + RTO certification (optional)', 'No (licence attestation only)'],
  ['Saudi Labour Visa', 'None (no degree required)', 'No'],
];

const STAMPING_STEPS = [
  {
    title: 'Step 1 — Submit Complete Visa Package to ZAFCO',
    points: [
      'Original passport (valid 6+ months)',
      'Saudi Work Permit Visa copy (from Muqeem)',
      'Electronic Wakala (from Qiwa)',
      'Wafid Medical for Saudi Visa clearance report',
      'Saudi Degree Attestation (HRD + MEA + Embassy stamped)',
      'PCC (if required by employer) — MEA-apostilled',
      'Passport-size photographs (2 copies)',
      'Visa application form (ZAFCO-provided)',
    ],
  },
  {
    title: 'Step 2 — ZAFCO Pre-Verification (1–2 days)',
    points: [
      'Document completeness check',
      'Saudi Work Permit Visa authenticity verified on Muqeem',
      'Wafid Medical for Saudi Visa report validated',
      'Saudi Degree Attestation has all 3 stamps',
      'If PCC required: MEA apostille verified',
      '100% compliance check before Embassy submission',
    ],
  },
  {
    title: 'Step 3 — ZAFCO Submits to Royal Saudi Embassy (Direct)',
    points: [
      'ZAFCO delivers the package to the Embassy counter (New Delhi)',
      'Embassy issues an Application Reference Number',
      'ZAFCO tracks status via the Embassy portal',
    ],
  },
  {
    title: 'Step 4 — Embassy Processing (3–5 days for Authorised Agencies)',
    points: [
      'Embassy verifies Saudi Work Permit Visa with MHRSD',
      'Validates Wafid Medical with GAMCA',
      'If PCC required: confirms with Indian police',
      'Cross-checks Saudi Degree Attestation with Indian universities',
      'If Technician: verifies QVP Clearance Report on the Qiwa platform',
      'If Nurse: verifies DataFlow Professional Verification report',
      'Final decision: visa approved or rejected',
    ],
  },
  {
    title: 'Step 5 — Visa Stamping & Passport Return',
    points: [
      'Approved: Embassy places the Saudi Work Visa stamp on the passport (3-page visa)',
      'Rejected: Embassy returns the passport with a rejection letter',
      'ZAFCO collects the passport within 24 hours of stamping',
      'Candidate receives the passport via next-day courier delivery',
    ],
  },
];

const CITY_CENTERS = [
  ['Saudi Work Visa Delhi', 'GF 5, 27B, Khizrabad, New Friends Colony, New Delhi 110025', 'Full visa processing, document attestation, medical coordination'],
  ['Saudi Visa Agency Delhi', 'Same as above', 'Direct Embassy submission'],
  ['Saudi Work Visa Mumbai', 'Andheri East, Mumbai 400059', 'Visa stamping, degree attestation, PCC support'],
  ['Saudi Work Visa Hyderabad', 'Banjara Hills, Hyderabad 500034', 'Qiwa contract verification, Wafid medical booking'],
  ['Saudi Work Visa Chennai', 'T. Nagar, Chennai 600034', 'Medical coordination, Embassy submission'],
  ['Saudi Work Visa Kolkata', 'Sector V, Kolkata 700091', 'Full visa processing, document verification'],
];

const PROCESSING_TIMELINE = [
  ['Qiwa Contract + Muqeem Work Permit', '3–5 days', 'Employer completes digital compliance'],
  ['QVP Verification (Technician only)', '9–13 business days', 'Institution verification (mandatory for Technician)'],
  ['Wafid / GAMCA Medical Testing', '1–2 days', 'Appointment + report generation'],
  ['Saudi Degree Attestation (HRD + MEA + Embassy)', '24–50 days', 'State + national + embassy verification'],
  ['PCC (if required by employer)', '24–45 days', 'Police verification + MEA processing'],
  ['Saudi Visa Stamping in India', '3–5 days (ZAFCO) / 15–20 days (general)', 'Authorised-agency priority queue'],
  ['TOTAL — Technician with QVP', '41–115 days (1.5–4 months)', 'QVP + attestation during processing'],
  ['TOTAL — Other categories, no QVP', '32–102 days (1–3.5 months)', 'No QVP requirement'],
];

const FEE_COMPONENTS = [
  'Visa stamping fee: SAR 300–500 (≈ INR 7,000–12,000) — varies by category',
  'Medical fee: INR 10,000–14,000 (Wafid / GAMCA)',
  'PCC fee (if required): INR 500 + MEA apostille INR 500',
  'Degree attestation: INR 3,500–6,500 per document (HRD + MEA + Embassy)',
  'Service charge: ZAFCO competitive agency fee (varies by package)',
];

const PRICING_FACTORS = [
  'MOFA fee changes: Saudi MOFA updates fees quarterly',
  'Currency exchange rates: SAR / INR fluctuation affects total cost',
  'Document complexity: degree attestation adds incremental costs',
  'Medical centre fees: Wafid / GAMCA centres vary by location (INR 10,000–14,000)',
  'Service charges: authorised agency fees (ZAFCO competitive pricing)',
  'PCC (if required): additional INR 1,000–1,500',
];

const FAQS = [
  {
    q: 'What happens if a candidate fails the Wafid / GAMCA medical test?',
    a: 'Immediate visa rejection — no stamping is possible. Permanent ban for HIV and Hepatitis B / C (no re-application). Temporary rejection for TB and Syphilis (6-month treatment + re-test allowed). 90-day wait period before re-application for temporary conditions. ZAFCO Medical Appeal Support: for TB / Syphilis cases, ZAFCO coordinates treatment scheduling + re-test booking at a Wafid centre. Prevention: ZAFCO pre-screens candidates for medical risks before Qiwa contract submission — reducing rejection rates by ~85%.',
  },
  {
    q: 'Is Police Clearance Certificate (PCC) still mandatory for Saudi Work Visa in 2026?',
    a: 'NO — PCC is NO LONGER mandatory for all Saudi Work Visa categories as of January 2026, per new MOFA guidelines. However, PCC may still be required if the Saudi employer explicitly requests it in the Qiwa contract, if the candidate has a previous criminal record, for government-sector employment in Saudi Arabia, or if MHRSD / MOFA flags the candidate for additional verification. If PCC is required, it must be valid for 6 months from issuance and MEA-apostilled for international validity.',
  },
  {
    q: 'Can a Labour Visa category be stamped with an Engineer\u2019s degree?',
    a: 'NO — this is "Category Misclassification" and leads to visa rejection. Saudi Labour Visa requires minimum 8th pass (no degree needed). Saudi Engineer Visa requires B.Tech / BE + Engineering Council registration. Mismatch: if the candidate has an Engineer\u2019s degree but applies for a Labour Visa, the Embassy rejects the application. Saudi MHRSD requires strict job-role ↔ qualification alignment. ZAFCO verifies the Saudi Employment Contract Visa on Qiwa to ensure the role matches the candidate\u2019s degree — preventing misclassification rejection.',
  },
  {
    q: 'What is QVP (Qualification Verification Program) and is it mandatory for Saudi Technician Visa?',
    a: 'YES — QVP is MANDATORY for ALL Saudi Technician Visa applicants as of 2026. QVP is the official MHRSD digital verification system for validating technical diplomas (ITI / Polytechnic). It is administered through the Qiwa platform and verifies diploma authenticity, program duration (must be 3+ years), institution recognition (AICTE / State Board), and prevents fraudulent diploma cases. Process: employer initiates QVP on Qiwa → institution verifies within 7–10 days → candidate receives QVP Clearance Report (PDF) → submit report to ZAFCO for visa stamping. Timeline: 9–13 business days. Cost: INR 700–1,200. Rejection reasons: fraudulent diploma, institution not recognised, or program duration < 3 years (permanent); certificate / marks mismatch (temporary — can clarify). Without a QVP Clearance Report, the Saudi Technician Visa will be REJECTED.',
  },
  {
    q: 'Can I start Saudi Work Visa Processing before receiving the Qiwa contract?',
    a: 'NO — the Qiwa contract is a mandatory prerequisite. The Saudi Work Permit Visa (from Muqeem) is required for medical booking, and the Electronic Wakala is required for Embassy submission. Without the Qiwa contract, the candidate cannot book a Wafid / GAMCA medical appointment. About 80% of rejection cases involve incomplete Qiwa / Muqeem compliance. ZAFCO verifies the Qiwa contract + Muqeem work permit before scheduling any candidate steps — ensuring 100% compliance.',
  },
  {
    q: 'How long does Saudi Degree Attestation take, and can it be done after the medical test?',
    a: 'Saudi Degree Attestation takes 24–50 days across 3 tiers: State HRD (7–15 days) + MEA Apostille (7–15 days) + Saudi Embassy Attestation (10–20 days). It can be done after the medical — attestation is independent of the medical test — but it MUST be completed before Embassy stamping (the Embassy rejects unattested degrees). Best practice: start attestation immediately after the Qiwa contract (in parallel with the medical). As an Authorised Agency, ZAFCO handles the complete attestation lifecycle and typically reduces the timeline to 30–35 days via direct Embassy access.',
  },
  {
    q: 'Why does the Royal Embassy of Saudi Arabia not accept direct individual submissions?',
    a: 'This is an Embassy security protocol to prevent fraud and ensure document authenticity. Direct submissions bypass verification (high fraud risk). Authorised agencies like ZAFCO pre-verify all documents before submission — the Embassy trusts authorised agencies and grants priority queue access (3–5 days vs 15–20 days for general applicants). Authorised agencies face penalties for non-compliant submissions, which keeps the system clean. As an Authorised Agency by the Royal Embassy, ZAFCO gets priority queue — 3–5 day stamping vs 15–20 days for general applicants.',
  },
  {
    q: 'What is the difference between GAMCA Medical for Saudi Visa and Wafid Medical for Saudi Visa?',
    a: 'GAMCA (old) used gateway.gamca.org.sa with manual booking, physical + email reports, 3–5 day processing, and 45 centres in India. Wafid (new 2025) uses www.wafid.com with online self-service booking, digital reports (instant download), 1–2 day processing, and 50+ centres in India. Medical requirements are IDENTICAL — only the platform changed. Both are valid for Saudi Work Visa from India.',
  },
  {
    q: 'What documents are required for Saudi Nurse Visa vs Saudi Technician Visa vs Saudi Engineer Visa?',
    a: 'Saudi Nurse Visa: B.Sc. Nursing / P.G. Diploma + Indian Nursing Council (INC) + State Nursing Council license; DataFlow Professional Verification MANDATORY; 2 years clinical experience; SAR 6,000–8,000. Saudi Technician Visa: 3-year diploma (ITI / Polytechnic / State Technical Board) + State Technical Education Board certification; QVP MANDATORY; 3 years technical experience; SAR 4,500–6,000. Saudi Engineer Visa: B.Tech / BE + Engineering Council (IEI) — no verification required apart from IEI; 5 years engineering experience; SAR 8,000+. All three require Wafid / GAMCA Medical for Saudi Visa, Saudi Degree Attestation, Qiwa contract, and Muqeem work permit. PCC only if employer requests.',
  },
];

/* Tone-styled callout box */
const Callout = ({ tone = 'amber', title, children, icon: Icon = AlertTriangle }) => {
  const tones = {
    amber: 'bg-amber-50 border-amber-200',
    blue: 'bg-blue-50 border-blue-200',
    red: 'bg-red-50 border-red-200',
    green: 'bg-emerald-50 border-emerald-200',
    slate: 'bg-slate-50 border-slate-200',
  };
  const iconTones = {
    amber: 'text-amber-700', blue: 'text-blue-700', red: 'text-red-700',
    green: 'text-emerald-700', slate: 'text-slate-700',
  };
  return (
    <div className={`rounded-xl border p-5 ${tones[tone] || tones.amber}`}>
      <div className="flex items-start gap-3 mb-2">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconTones[tone] || iconTones.amber}`} aria-hidden="true" />
        <h4 className="font-bold text-zafco-blue">{title}</h4>
      </div>
      <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
    </div>
  );
};

const SaudiWorkVisaPage = () => {
  const ch = useContactChannel();

  usePageMeta(
    'Saudi Work Visa from India 2026 | Authorized Embassy Agency ZAFCO | MEA & Qiwa Compliant',
    'Get Saudi Work Visa from India 2026 with Authorized Embassy Agency ZAFCO. MEA & Qiwa compliant. QVP for Technician visas. 98% success, 400K+ visas. Professional, Engineer, Technician, Nurse, Labor visas.',
    {
      canonical: '/saudi-arabia/work-visa',
      image: '/social-media/zafco-facebook-cover.png',
      imageAlt: 'Saudi Work Visa from India 2026 — ZAFCO Authorized Embassy Agency, MEA & Qiwa compliant.',
    },
  );

  useFAQSchema(FAQS);

  // Breadcrumb + Service JSON-LD
  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Saudi Arabia', item: 'https://zafco.in/saudi-arabia' },
        { '@type': 'ListItem', position: 3, name: 'Saudi Work Visa', item: 'https://zafco.in/saudi-arabia/work-visa' },
      ],
    };
    const service = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Saudi Work Visa Processing for Indians',
      description: 'MEA-approved & Royal Saudi Embassy authorised Saudi Work Visa processing for Indian nationals — Professional, Engineer, Technician (QVP), Nurse (DataFlow), Driver, and Labour visas. Includes Qiwa / Muqeem compliance, GAMCA / Wafid medical coordination, degree attestation, PCC, and direct Embassy stamping.',
      serviceType: 'Visa Processing',
      provider: { '@id': 'https://zafco.in/#organization' },
      areaServed: [{ '@type': 'Country', name: 'India' }, { '@type': 'Country', name: 'Saudi Arabia' }],
      audience: { '@type': 'Audience', audienceType: 'Indian professionals, technicians, nurses, drivers and labour seeking employment in Saudi Arabia' },
      url: 'https://zafco.in/saudi-arabia/work-visa',
    };
    const inject = (data, id) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-saudi-work-page', id);
      el.text = JSON.stringify(data).replace(/<\/(script)/gi, '<\\/$1');
      document.head.appendChild(el);
      return el;
    };
    const els = [inject(breadcrumb, 'breadcrumb'), inject(service, 'service')];
    return () => { els.forEach((el) => el?.remove()); };
  }, []);

  const contactQuery = '?source=saudi-work-visa';
  const waMessage = 'Hello ZAFCO, I want to apply for a Saudi Work Visa from India. Please share next steps and a customised quote.';

  return (
    <div data-testid="saudi-work-visa-page">
      <PageHeader
        title="Saudi Work Visa from India 2026 — Authorized Embassy Agency ZAFCO"
        subtitle="MEA & Qiwa compliant · 98% success rate · 15+ years experience · 400,000+ visas processed."
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Saudi Arabia', path: '/saudi-arabia' },
          { label: 'Saudi Work Visa', path: '/saudi-arabia/work-visa' },
        ]}
      />

      {/* Government Approval Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <Shield className="w-8 h-8 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-bold text-lg">Government of India Approved · Royal Saudi Embassy Authorised Agency</p>
              <p className="text-green-100 text-sm">MEA Registration Certificate: {MEA_REG}</p>
            </div>
            <Award className="w-8 h-8 flex-shrink-0 hidden md:block" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* SECTION 1 — Executive Overview */}
      <section className="py-16 bg-white" aria-labelledby="saudi-work-intro">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Vision 2030 · Saudi Talent Migration</span>
          <h2 id="saudi-work-intro" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            Saudi Arabia Work Visa Overview &amp; Basic Entry — Jobs for Indians
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            The Kingdom of Saudi Arabia has emerged as the world&rsquo;s most dynamic employment destination for Indian professionals, driven by the audacious <strong>Vision 2030</strong> transformation agenda. Saudi Arabia is investing <strong>$1.3 trillion</strong> in infrastructure, healthcare, technology, energy, and construction projects — creating over <strong>2.5 million new job opportunities</strong> for foreign nationals, with Indian workers comprising <strong>35% of the total expatriate workforce</strong>.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            As of 2026, over <strong>1.8 million Indian nationals</strong> are legally employed across Saudi Arabia, spanning engineering, healthcare, construction, manufacturing, logistics, and technical services. The Saudi government has streamlined immigration protocols through digital platforms like <strong>Qiwa, MHRSD, and Muqeem</strong>, reducing processing timelines by 60% while maintaining rigorous compliance standards.
          </p>

          <h3 className="text-2xl font-bold text-zafco-blue font-syne mt-10 mb-4">
            ZAFCO — The Premier Saudi Recruitment Agency & Officially Authorised Embassy Partner
          </h3>
          <p className="text-gray-700 leading-relaxed mb-6">
            <Link to="/" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">ZAFCO Human Resource Management</Link> stands as the only officially <strong>Authorised Agency by the Royal Embassy of Saudi Arabia</strong> for visa stamping and processing in India. This exclusive authorisation grants ZAFCO direct, secure, and expedited access to Embassy channels — eliminating third-party delays and ensuring a <strong>98% visa approval success rate</strong> across <strong>400,000+ processed visas</strong>. As a Government of India MEA-Approved Recruitment Agency (RC: {MEA_REG}) with 15+ years of expertise, ZAFCO bridges Indian talent with Saudi employers via:
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {ZAFCO_ADVANTAGES.map((item) => (
              <div key={item.label} className="flex items-start gap-3 bg-slate-50 rounded-xl border border-slate-200 p-4">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <div>
                  <h4 className="font-bold text-zafco-blue">{item.label}</h4>
                  <p className="text-sm text-gray-700">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-gray-700 leading-relaxed mb-8">
            If you are seeking <strong>Saudi Arabia Jobs for Indians</strong>, ZAFCO is your trusted partner for Saudi Work Visa, Saudi Employment Visa, and Saudi Visa Stamping in India — backed by official Embassy authorisation and MEA compliance. See also our{' '}
            <Link to="/saudi-arabia/business-visa" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">Saudi Business Visit Visa</Link> and{' '}
            <Link to="/saudi-arabia/tourist-visa" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">Saudi Tourist Visa</Link> guides.
          </p>

          <p className="text-gray-700 leading-relaxed mb-8">
            For advanced 2026 compliance — QVP verification workflows, GAMCA/Wafid sequencing, SSCO occupation coding, and Wakala authorization — study the{' '}
            <Link to="/services/saudi-work-visa-qvp-attestation/" data-testid="old-work-link-qvp" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">2026 Saudi Work Visa & QVP Attestation Master Guide</Link>, and for the Kingdom&rsquo;s full regulatory architecture (Qiwa, Mudad, Nitaqat, Absher), see the{' '}
            <Link to="/saudi-visa-framework-2026/" data-testid="old-work-link-hub" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">Saudi Labor Compliance & Visa Framework 2026</Link>.
          </p>

          {/* Primary CTAs */}
          <div className="flex flex-wrap gap-3">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="saudi-work-hero-cta-apply">
              <Link to={`/contact${contactQuery}&intent=apply`}>
                <ArrowRight className="w-4 h-4 mr-2" aria-hidden="true" /> Get Customised Quote Now
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-zafco-blue text-zafco-blue hover:bg-zafco-blue/5" data-testid="saudi-work-hero-cta-whatsapp">
              <a href={ch.whatsappUrlWith(waMessage)} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 mr-2" aria-hidden="true" /> WhatsApp Us
              </a>
            </Button>
            <Button asChild variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50" data-testid="saudi-work-hero-cta-call">
              <a href={ch.phoneTelUrl}>
                <Phone className="w-4 h-4 mr-2" aria-hidden="true" /> Call {ch.phoneDisplay}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Visa Types */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-work-types">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Visa Categories</span>
          <h2 id="saudi-work-types" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Types of Saudi Work Visas — Role-Specific Requirements & Document Protocols
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            The Saudi Ministry of Human Resources and Social Development (MHRSD) categorises employment visas based on qualifications, skill levels, and job functions. Each category has distinct vetting requirements, attestation protocols, and medical standards. Understanding your exact visa category is critical for Saudi Work Visa Processing success.
          </p>

          <div className="space-y-8">
            {VISA_TYPES.map((opt) => {
              const Icon = opt.icon;
              return (
                <motion.div
                  key={opt.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-zafco-blue/10 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-zafco-blue" aria-hidden="true" />
                      </div>
                      <h3 className="font-bold text-zafco-blue text-lg">{opt.title}</h3>
                    </div>
                    <span className={`inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full ${opt.badgeTone}`}>
                      {opt.badge}
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-5">{opt.intro}</p>

                  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white mb-5">
                    <table className="min-w-full text-sm">
                      <thead className="bg-zafco-blue/5 text-zafco-blue">
                        <tr>
                          {opt.isComparison
                            ? opt.headers.map((h) => <th key={h} className="px-4 py-2.5 text-left font-bold">{h}</th>)
                            : (<>
                                <th className="px-4 py-2.5 text-left font-bold">Requirement</th>
                                <th className="px-4 py-2.5 text-left font-bold">Specification</th>
                              </>)
                          }
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {opt.rows.map((r, i) => (
                          <tr key={i}>
                            {r.map((cell, ci) => (
                              <td key={ci} className={`px-4 py-2.5 ${ci === 0 ? 'font-semibold text-zafco-blue align-top' : 'text-gray-700'}`}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {opt.docs && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 mb-5">
                      <h4 className="font-bold text-zafco-blue text-sm uppercase tracking-wider mb-3">Key Document Checklist</h4>
                      <ul className="grid sm:grid-cols-2 gap-2 text-sm text-gray-700">
                        {opt.docs.map((d) => (
                          <li key={d} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {opt.note && (
                    <Callout tone={opt.noteTone || 'amber'} title="Important Note">
                      {opt.note}
                    </Callout>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* QVP DEEP DIVE — Inside Visa Types Section */}
          <div className="mt-12 rounded-2xl border-2 border-red-200 bg-red-50 p-6 md:p-8">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-600 text-white flex items-center justify-center flex-shrink-0">
                <Wrench className="w-6 h-6" aria-hidden="true" />
              </div>
              <div>
                <span className="text-red-700 font-bold text-xs tracking-widest uppercase">Mandatory for ALL Technician Visas</span>
                <h3 className="text-2xl md:text-3xl font-bold text-zafco-blue font-syne mt-1">
                  QVP — Qualification Verification Program (Complete Guide)
                </h3>
              </div>
            </div>

            <h4 className="font-bold text-zafco-blue text-lg mb-2">What is QVP?</h4>
            <p className="text-gray-700 leading-relaxed mb-5">
              <strong>QVP (Qualification Verification Program)</strong> is the official MHRSD digital verification system launched by Saudi Arabia in 2023 to validate the authenticity of technical diplomas, vocational certificates, and trade qualifications for Saudi Technician Visa applicants. QVP is administered through the Qiwa platform and is <strong>MANDATORY for ALL Technician visa categories as of 2026</strong>.
            </p>

            <h4 className="font-bold text-zafco-blue text-base mb-2">QVP Purpose</h4>
            <ul className="space-y-1.5 text-sm text-gray-700 mb-6">
              {[
                'Verify authenticity of technical diplomas (ITI, Polytechnic, State Technical Board)',
                'Confirm program duration matches Saudi job requirements (minimum 3 years for Technician)',
                'Validate institution recognition (AICTE / State Technical Education Board approved)',
                'Prevent fraudulent diploma / substitution cases in Saudi work visa processing',
                'Ensure skill-level alignment with job role (electrician, welder, mechanic, HVAC, etc.)',
              ].map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-xl border border-red-200 p-4">
                <h4 className="font-bold text-zafco-blue text-sm uppercase tracking-wider mb-3">QVP REQUIRED — All Technician Categories</h4>
                <ul className="grid grid-cols-2 gap-1.5 text-xs text-gray-700">
                  {QVP_REQUIRED.map((p) => (
                    <li key={p} className="flex items-start gap-1.5">
                      <ChevronRight className="w-3.5 h-3.5 text-red-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-xl border border-emerald-200 p-4">
                <h4 className="font-bold text-zafco-blue text-sm uppercase tracking-wider mb-3">QVP NOT Required</h4>
                <ul className="space-y-1.5 text-xs text-gray-700">
                  {QVP_NOT_REQUIRED.map((p) => (
                    <li key={p} className="flex items-start gap-1.5">
                      <XCircle className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <h4 className="font-bold text-zafco-blue text-base mb-3">Complete QVP Process — Step-by-Step</h4>
            <div className="space-y-3 mb-6">
              {QVP_STEPS.map((s, i) => (
                <div key={s.title} className="rounded-xl bg-white border border-red-200 p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-7 h-7 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                    <h5 className="font-bold text-zafco-blue text-sm">{s.title}</h5>
                  </div>
                  <ul className="space-y-1.5 text-sm text-gray-700 ml-10">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <h4 className="font-bold text-zafco-blue text-base mb-3">QVP Rejection Scenarios — What Causes Failure?</h4>
            <div className="overflow-x-auto rounded-xl border border-red-200 bg-white mb-6">
              <table className="min-w-full text-sm">
                <thead className="bg-red-100 text-zafco-blue">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-bold">Rejection Reason</th>
                    <th className="px-4 py-2.5 text-left font-bold">Status</th>
                    <th className="px-4 py-2.5 text-left font-bold">Appeal Option</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-100">
                  {QVP_REJECTIONS.map((r) => (
                    <tr key={r[0]}>
                      <td className="px-4 py-2.5 font-semibold text-zafco-blue">{r[0]}</td>
                      <td className="px-4 py-2.5 text-gray-700">{r[1]}</td>
                      <td className="px-4 py-2.5 text-gray-700">{r[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h4 className="font-bold text-zafco-blue text-base mb-3">QVP Timeline & Costs</h4>
            <div className="overflow-x-auto rounded-xl border border-red-200 bg-white mb-6">
              <table className="min-w-full text-sm">
                <thead className="bg-red-100 text-zafco-blue">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-bold">Phase</th>
                    <th className="px-4 py-2.5 text-left font-bold">Timeline</th>
                    <th className="px-4 py-2.5 text-left font-bold">Fee (Approx)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-100">
                  {QVP_TIMELINE.map((r, i) => (
                    <tr key={r[0]} className={i === QVP_TIMELINE.length - 1 ? 'bg-amber-50 font-bold' : ''}>
                      <td className="px-4 py-2.5 font-semibold text-zafco-blue">{r[0]}</td>
                      <td className="px-4 py-2.5 text-gray-700">{r[1]}</td>
                      <td className="px-4 py-2.5 text-gray-700">{r[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Callout tone="green" title="ZAFCO's QVP Expertise Advantage" icon={BadgeCheck}>
              As an Authorised Agency by the Royal Embassy of Saudi Arabia, ZAFCO has direct Qiwa platform access for QVP verification. We:
              <ul className="mt-2 space-y-1.5">
                {[
                  'Initiate QVP process on behalf of the employer (if requested)',
                  'Track QVP status in real-time via the Qiwa portal',
                  'Contact issuing institutions if QVP is delayed beyond 7 days',
                  'Verify QVP Clearance Report authenticity before Embassy submission',
                  'Ensure 100% QVP compliance — eliminating rejection risks',
                ].map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </Callout>

            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <Callout tone="red" title="Critical — Saudi Technician Visa (QVP Mandatory)">
                QVP is MANDATORY for ALL Saudi Technician Visa applicants as of 2026. Incomplete or failed QVP verification results in immediate visa application rejection. Candidates must ensure: 3-year duration diploma (ITI / Polytechnic / State Technical Board); institution AICTE or State Board recognised; specialisation aligned with job role; certificate number, marks, and dates match institutional records.
              </Callout>
              <Callout tone="amber" title="Critical — Saudi Nurse Visa (DataFlow Mandatory)" icon={Stethoscope}>
                Nursing professionals must have valid Indian Nursing Council (INC) registration and State Nursing Council license. DataFlow Professional Verification is MANDATORY for all nurse candidates — incomplete verification results in Saudi Work Visa Application rejection.
              </Callout>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — Step 1: Digital Compliance */}
      <section className="py-16 bg-white" aria-labelledby="saudi-work-step1">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Step 1 · Digital Compliance</span>
          <h2 id="saudi-work-step1" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Pre-requisites & Digital Compliance — Qiwa Platform & Electronic Wakala
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Before initiating Saudi Work Visa from India, the Saudi employer must complete critical digital compliance steps on Saudi government platforms. This is the most critical phase — incomplete digital preparation leads to ~90% of visa rejection cases.
          </p>

          <h3 className="text-xl font-bold text-zafco-blue mb-3">A. Qiwa Platform — Saudi Employment Contract Visa Generation</h3>
          <p className="text-gray-700 mb-5">
            The <a href="https://qiwa.mhrsd.gov.sa" target="_blank" rel="noopener noreferrer" className="text-zafco-blue underline">Qiwa platform</a> is the official MHRSD digital portal for employment contract registration. All Saudi work permits, Saudi job visas, and Saudi Employment Contract visas must be generated here.
          </p>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 mb-6">
            <h4 className="font-bold text-zafco-blue mb-4">Step-by-Step Qiwa Contract Process</h4>
            <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside marker:font-bold marker:text-amber-600">
              {QIWA_STEPS.map((s) => (<li key={s} className="leading-relaxed">{s}</li>))}
            </ol>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 mb-6">
            <h4 className="font-bold text-zafco-blue mb-3">Critical Qiwa Compliance Requirements</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              {QIWA_COMPLIANCE.map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <Callout tone="green" title="ZAFCO's Qiwa Mastery Advantage" icon={BadgeCheck}>
            As an Authorised Agency by the Royal Embassy of Saudi Arabia, ZAFCO has direct Qiwa platform access for contract verification. We validate the Saudi Employment Contract Visa authenticity before candidate submission, ensuring 100% contract compliance and eliminating rejection risks.
          </Callout>

          <h3 className="text-xl font-bold text-zafco-blue mb-3 mt-10">B. Muqeem Platform — Electronic Wakala & Work Permit Validation</h3>
          <p className="text-gray-700 mb-5">
            The <a href="https://muqeem.mofa.gov.sa" target="_blank" rel="noopener noreferrer" className="text-zafco-blue underline">Muqeem platform</a> is MOFA&rsquo;s portal for visa authorisation and work permit validation.
          </p>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 mb-6">
            <h4 className="font-bold text-zafco-blue mb-4">Muqeem Workflow</h4>
            <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside marker:font-bold marker:text-amber-600">
              {MUQEEM_STEPS.map((s) => (<li key={s} className="leading-relaxed">{s}</li>))}
            </ol>
          </div>

          <Callout tone="amber" title="Why This Matters for Saudi Work Visa from India">
            Without a valid Muqeem Work Permit, Saudi Visa Stamping in India cannot proceed. ZAFCO verifies Muqeem Work Permit status before scheduling Embassy submission — preventing costly delays.
          </Callout>
        </div>
      </section>

      {/* SECTION 4 — Step 2: Medical */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-work-step2">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Step 2 · Medical Vetting</span>
          <h2 id="saudi-work-step2" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            GAMCA Medical for Saudi Visa & Wafid Medical for Saudi Visa
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            All Saudi Visa for Indian Citizens seeking employment visas must undergo mandatory medical screening at GAMCA / Wafid-certified medical centres. This is a non-negotiable requirement — no medical clearance = no visa stamping.
          </p>

          <h3 className="text-xl font-bold text-zafco-blue mb-3">GAMCA → Wafid: The 2025 Transformation</h3>
          <p className="text-gray-700 mb-5">
            In January 2025, Saudi Arabia transitioned from <strong>GAMCA Medical for Saudi Visa</strong> (GCC Approved Medical Centers Association) to <strong>Wafid Medical for Saudi Visa</strong> (new digital platform). While the platform changed, medical requirements remain identical.
          </p>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white mb-6">
            <table className="min-w-full text-sm">
              <thead className="bg-zafco-blue/5 text-zafco-blue">
                <tr>
                  <th className="px-4 py-2.5 text-left font-bold">Aspect</th>
                  <th className="px-4 py-2.5 text-left font-bold">GAMCA (Old)</th>
                  <th className="px-4 py-2.5 text-left font-bold">Wafid (New 2026)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {MEDICAL_COMPARISON.map((r) => (
                  <tr key={r[0]}>
                    <td className="px-4 py-2.5 font-semibold text-zafco-blue">{r[0]}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r[1]}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Callout tone="amber" title="Key Takeaway">
            Wafid Medical for Saudi Visa is faster, more digital, and has more centres — but Saudi Visa Medical Requirements remain unchanged.
          </Callout>

          <h3 className="text-xl font-bold text-zafco-blue mb-4 mt-10">Step-by-Step Saudi Medical Test for Employment Visa</h3>
          <div className="space-y-4 mb-8">
            {MEDICAL_STEPS.map((s, i) => (
              <div key={s.title} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-zafco-blue text-white text-sm font-bold flex items-center justify-center">{i + 1}</div>
                  <h4 className="font-bold text-zafco-blue">{s.title}</h4>
                </div>
                <ul className="space-y-1.5 text-sm text-gray-700 ml-11">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-zafco-blue mb-4">Saudi Visa Medical Requirements — What Causes Rejection?</h3>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white mb-6">
            <table className="min-w-full text-sm">
              <thead className="bg-zafco-blue/5 text-zafco-blue">
                <tr>
                  <th className="px-4 py-2.5 text-left font-bold">Condition</th>
                  <th className="px-4 py-2.5 text-left font-bold">Status</th>
                  <th className="px-4 py-2.5 text-left font-bold">Appeal Option</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {MEDICAL_REJECTIONS.map((r) => (
                  <tr key={r[0]}>
                    <td className="px-4 py-2.5 font-semibold text-zafco-blue">{r[0]}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r[1]}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Callout tone="amber" title="What Happens if Candidate Fails Wafid / GAMCA Medical?">
            Immediate visa rejection — no stamping possible. 90-day wait period before re-application (for temporary conditions). Permanent ban for HIV / Hepatitis (no re-application). <strong>ZAFCO Medical Appeal Support</strong>: For TB / Syphilis cases, ZAFCO coordinates treatment + re-test scheduling.
          </Callout>
        </div>
      </section>

      {/* SECTION 5 — Step 3: PCC */}
      <section className="py-16 bg-white" aria-labelledby="saudi-work-step3">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Step 3 · Police Clearance</span>
          <h2 id="saudi-work-step3" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            Legal & Criminal Background Checks — Police Clearance Certificate (PCC) Requirements
          </h2>

          <Callout tone="green" title="Important Update for 2026" icon={BadgeCheck}>
            The Police Clearance Certificate (PCC) is <strong>NO LONGER a mandatory requirement</strong> for all Saudi Work Visa categories as of January 2026, per new MOFA guidelines. This change applies to Professional, Engineer, Technician, Nurse, Driver, and Labour visa categories.
          </Callout>

          <p className="text-gray-700 leading-relaxed mt-6 mb-4">
            However, PCC may still be required in specific cases:
          </p>
          <ul className="space-y-2 text-sm text-gray-700 mb-8">
            {PCC_TRIGGERS.map((p) => (
              <li key={p} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>{p}</span>
              </li>
            ))}
          </ul>

          <h3 className="text-xl font-bold text-zafco-blue mb-4">How to Obtain PCC from Passport Seva Kendra (PSK) — If Required</h3>
          <div className="space-y-4 mb-8">
            {PCC_STEPS.map((s, i) => (
              <div key={s.title} className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-zafco-blue text-white text-sm font-bold flex items-center justify-center">{i + 1}</div>
                  <h4 className="font-bold text-zafco-blue">{s.title}</h4>
                </div>
                <ul className="space-y-1.5 text-sm text-gray-700 ml-11">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-zafco-blue mb-3">MEA Apostille Requirement for PCC</h3>
          <p className="text-gray-700 mb-4">
            If PCC is required by the employer, the certificate must be apostilled by the Ministry of External Affairs (MEA) for international validity. Process: PCC → State HRD → MEA Apostille (7–15 days, INR 500 per document).
          </p>
          <Callout tone="amber" title="Why MEA Apostille is Critical (If PCC Required)">
            Without MEA apostille, Saudi Embassy Attestation will reject the PCC. ZAFCO handles the complete MEA apostille process — no candidate effort required.
          </Callout>
        </div>
      </section>

      {/* SECTION 6 — Step 4: Attestation */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-work-step4">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Step 4 · Degree Attestation</span>
          <h2 id="saudi-work-step4" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Academic & Certificate Attestation Lifecycle — Saudi Degree Attestation & Saudi Embassy Attestation
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Saudi Degree Attestation is the most complex phase of Saudi Work Visa Processing — a 3-tier verification chain: State HRD → MEA → Saudi Embassy.
          </p>

          <div className="space-y-5 mb-8">
            {ATTESTATION_LIFECYCLE.map((t, i) => (
              <div key={t.tier} className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-zafco-blue text-white flex items-center justify-center font-bold text-sm">{i + 1}</div>
                    <h3 className="font-bold text-zafco-blue text-lg">{t.tier}</h3>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                    <Clock className="w-3.5 h-3.5" aria-hidden="true" /> {t.duration}
                  </span>
                </div>
                <ul className="space-y-2 text-sm text-gray-700">
                  {t.steps.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-zafco-blue mb-4">Total Saudi Degree Attestation Timeline & Costs</h3>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white mb-6">
            <table className="min-w-full text-sm">
              <thead className="bg-zafco-blue/5 text-zafco-blue">
                <tr>
                  <th className="px-4 py-2.5 text-left font-bold">Phase</th>
                  <th className="px-4 py-2.5 text-left font-bold">Timeline</th>
                  <th className="px-4 py-2.5 text-left font-bold">Fee (Approx)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ATTESTATION_TIMELINE.map((r, i) => (
                  <tr key={r[0]} className={i === ATTESTATION_TIMELINE.length - 1 ? 'bg-amber-50 font-bold' : ''}>
                    <td className="px-4 py-2.5 font-semibold text-zafco-blue">{r[0]}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r[1]}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Callout tone="green" title="Why ZAFCO's Authorised Status Ensures Precision" icon={BadgeCheck}>
            As an Authorised Agency by the Royal Embassy of Saudi Arabia, ZAFCO has direct Embassy access for Saudi Embassy Attestation. We eliminate third-party delays, ensuring ~95% faster attestation compared to general applicants.
          </Callout>

          <h3 className="text-xl font-bold text-zafco-blue mb-4 mt-10">Documents Requiring Saudi Degree Attestation</h3>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-zafco-blue/5 text-zafco-blue">
                <tr>
                  <th className="px-4 py-2.5 text-left font-bold">Visa Category</th>
                  <th className="px-4 py-2.5 text-left font-bold">Documents to Attest</th>
                  <th className="px-4 py-2.5 text-left font-bold">Verification Required?</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ATTESTATION_DOCS_BY_CATEGORY.map((r) => (
                  <tr key={r[0]}>
                    <td className="px-4 py-2.5 font-semibold text-zafco-blue">{r[0]}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r[1]}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* SECTION 7 — Step 5: Visa Stamping */}
      <section className="py-16 bg-white" aria-labelledby="saudi-work-step5">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Step 5 · Visa Stamping</span>
          <h2 id="saudi-work-step5" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Final Submission, Processing & Stamping Protocol — Saudi Visa Stamping in India
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            The final phase is <strong>Saudi Visa Stamping in India</strong> — physical placement of the work visa on the candidate&rsquo;s passport by the Royal Embassy of Saudi Arabia.
          </p>

          <Callout tone="red" title="Critical Embassy Rule — No Direct Individual Submissions" icon={XCircle}>
            The Royal Embassy of Saudi Arabia does NOT accept direct individual applications. All Saudi Work Visa from India applications must go through an Authorised Saudi Visa Processing Agency.
          </Callout>

          <div className="mt-6 rounded-2xl bg-emerald-50 border-2 border-emerald-200 p-6">
            <h3 className="font-bold text-zafco-blue text-lg mb-3 flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-emerald-600" /> This is Why ZAFCO&rsquo;s Authorisation is Critical
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              {[
                'Direct Embassy submission: ZAFCO submits directly — no third-party delays',
                'Expedited processing: authorised agencies get priority queue (3–5 days vs 15–20 days)',
                '100% document compliance: ZAFCO pre-verifies all documents before submission',
                'Real-time status tracking: Embassy provides ZAFCO with live application updates',
              ].map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <h3 className="text-xl font-bold text-zafco-blue mb-4 mt-10">Saudi Visa Stamping in India — Complete Process Flow</h3>
          <div className="space-y-4 mb-10">
            {STAMPING_STEPS.map((s, i) => (
              <div key={s.title} className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-zafco-blue text-white text-sm font-bold flex items-center justify-center">{i + 1}</div>
                  <h4 className="font-bold text-zafco-blue">{s.title}</h4>
                </div>
                <ul className="space-y-1.5 text-sm text-gray-700 ml-11">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-zafco-blue mb-4">ZAFCO&rsquo;s Pan-India Operational Network</h3>
          <p className="text-gray-700 mb-5">ZAFCO operates dedicated Saudi Visa Processing Centres in 5 major cities — ensuring local accessibility for all candidates:</p>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white mb-6">
            <table className="min-w-full text-sm">
              <thead className="bg-zafco-blue/5 text-zafco-blue">
                <tr>
                  <th className="px-4 py-2.5 text-left font-bold">City</th>
                  <th className="px-4 py-2.5 text-left font-bold">ZAFCO Centre</th>
                  <th className="px-4 py-2.5 text-left font-bold">Services Offered</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {CITY_CENTERS.map((r) => (
                  <tr key={r[0]}>
                    <td className="px-4 py-2.5 font-semibold text-zafco-blue">{r[0]}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r[1]}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Callout tone="green" title="Why Choose ZAFCO for Saudi Work Visa Delhi / Mumbai / Hyderabad / Chennai / Kolkata?" icon={MapPin}>
            <ul className="mt-2 space-y-1.5">
              {[
                'Nearest centre: choose the ZAFCO centre closest to your location',
                'Same processing: all centres follow identical Embassy-approved protocols',
                '24/7 local support: dedicated agents in each city',
                'Free document pickup: ZAFCO collects documents from your home / office',
              ].map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </Callout>
        </div>
      </section>

      {/* SECTION 8 — Processing Time & Fees */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-work-time-fees">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Timeline & Fees</span>
          <h2 id="saudi-work-time-fees" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Saudi Work Visa Processing Time & Saudi Employment Visa Cost / Saudi Work Visa Fees
          </h2>
          <p className="text-gray-700 mb-6">Standard processing windows end-to-end across the major phases.</p>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white mb-6">
            <table className="min-w-full text-sm">
              <thead className="bg-zafco-blue/5 text-zafco-blue">
                <tr>
                  <th className="px-4 py-2.5 text-left font-bold">Phase</th>
                  <th className="px-4 py-2.5 text-left font-bold">Timeline</th>
                  <th className="px-4 py-2.5 text-left font-bold">Dependencies</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {PROCESSING_TIMELINE.map((r, i) => (
                  <tr key={r[0]} className={i >= PROCESSING_TIMELINE.length - 2 ? 'bg-amber-50 font-bold' : ''}>
                    <td className="px-4 py-2.5 font-semibold text-zafco-blue">{r[0]}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r[1]}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-10">
            <Callout tone="green" title="Fastest Timeline (Optimal Case)" icon={Clock}>
              7–10 days (rare — requires pre-prepared documents): all docs ready, employer completes Qiwa/Muqeem in 3 days, medical in 1 day, Embassy in 3 days (ZAFCO authorised).
            </Callout>
            <Callout tone="amber" title="Average Timeline (Realistic Case)" icon={Clock}>
              35–50 days (1–1.5 months — most common): attestation during processing, employer in 5 days, medical in 2 days, Embassy in 5 days.
            </Callout>
            <Callout tone="blue" title="Timeline with PCC Required" icon={Clock}>
              60–90 days (2–3 months): PCC + attestation during processing, employer in 5 days, medical in 2 days, PCC in 24–45 days, Embassy in 5 days.
            </Callout>
          </div>

          <h3 className="text-xl font-bold text-zafco-blue mb-4">Saudi Employment Visa Cost / Saudi Work Visa Fees — Dynamic Pricing Policy</h3>
          <Callout tone="amber" title="⚠️ Saudi Work Visa Fees are NOT Fixed">
            Saudi Employment Visa Cost and Saudi Work Visa Fees are dynamic and subject to:
            <ul className="mt-2 space-y-1.5">
              {PRICING_FACTORS.map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </Callout>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
            <h4 className="font-bold text-zafco-blue mb-3">Fee Structure Breakdown (Approximate Components)</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              {FEE_COMPONENTS.map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-600 italic mt-4">⚠️ DO NOT rely on fixed online quotes — fees change monthly.</p>
          </div>

          <div className="mt-6 rounded-2xl bg-gradient-to-r from-zafco-blue to-zafco-blue/90 text-white p-6">
            <h4 className="font-syne font-bold text-xl mb-3">Get an Immediate Customised Quote from ZAFCO</h4>
            <ul className="space-y-1.5 text-sm text-white/90 mb-4">
              {[
                'Real-time Saudi Work Visa Fees based on current MOFA rates',
                'Customised quote for your specific category (Professional / Technician / Labour)',
                'Complete cost breakdown (visa + medical + attestation + PCC if needed + service charges)',
                'No hidden charges — transparent pricing from an Authorised Agency by the Royal Embassy',
              ].map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="bg-amber-500 hover:bg-amber-600 text-zafco-blue font-bold" data-testid="saudi-work-quote-cta-apply">
                <Link to={`/contact${contactQuery}&intent=quote`}>Get Customised Quote <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild variant="outline" className="bg-white text-zafco-blue border-white hover:bg-white/90" data-testid="saudi-work-quote-cta-whatsapp">
                <a href={ch.whatsappUrlWith(waMessage)} target="_blank" rel="noopener noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 9 — Consultation Form */}
      <section className="py-16 bg-white" aria-labelledby="saudi-work-consultation">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Apply Now</span>
              <h2 id="saudi-work-consultation" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
                Start your Saudi Work Visa application today
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                As the only officially Authorised Agency by the Royal Embassy of Saudi Arabia for visa stamping in India, ZAFCO delivers end-to-end Saudi Work Visa Processing with a 98% success rate across 400,000+ visas.
              </p>
              <ul className="space-y-2 text-sm text-gray-700 mb-6">
                {[
                  'Real-time Qiwa & Muqeem verification',
                  'Complete QVP (Technician) / DataFlow (Nurse) handling',
                  'GAMCA / Wafid medical coordination at 50+ centres',
                  'Saudi Degree Attestation: HRD + MEA + Embassy lifecycle',
                  '3–5 day Embassy stamping (priority queue for authorised agencies)',
                  'Hassle-free document pickup across 5 Pan-India centres',
                ].map((p) => (
                  <li key={p} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> {p}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-theme="light">
              <ConsultationForm source="saudi-work-visa" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 10 — FAQs */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-work-faqs">
        <div className="container mx-auto px-6 max-w-4xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">FAQs · 9 Technical Questions</span>
          <h2 id="saudi-work-faqs" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-8">
            Addressing real Saudi Work Visa hurdles
          </h2>
          <div className="space-y-3">
            {FAQS.map((item, idx) => (
              <details key={item.q} className="group rounded-xl border border-slate-200 bg-white open:shadow-sm transition-all" data-testid={`saudi-work-faq-${idx}`}>
                <summary className="cursor-pointer list-none flex items-start justify-between gap-3 p-5 font-semibold text-zafco-blue">
                  <span className="flex-1">Q{idx + 1}: {item.q}</span>
                  <ChevronRight className="w-5 h-5 flex-shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
                </summary>
                <div className="px-5 pb-5 text-sm text-gray-700 leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 11 — Final CTA */}
      <section className="py-16 bg-zafco-blue text-white" aria-labelledby="saudi-work-final-cta">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 id="saudi-work-final-cta" className="text-3xl md:text-4xl font-bold font-syne mb-4">
            Contact ZAFCO — Your Authorised Saudi Visa Processing Agency
          </h2>
          <p className="text-white/90 leading-relaxed mb-6 max-w-2xl mx-auto">
            Get real-time Saudi Work Visa Fees, complete cost breakdown, and a personalised processing timeline from ZAFCO&rsquo;s Saudi Employment Visa Consultants.
          </p>
          <div className="grid md:grid-cols-3 gap-3 text-sm text-white/90 mb-8 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2"><Phone className="w-4 h-4" /> <a href={ch.phoneTelUrl} className="hover:underline">{ch.phoneDisplay}</a></div>
            <div className="flex items-center justify-center gap-2"><Mail className="w-4 h-4" /> <a href={`mailto:${ch.email}`} className="hover:underline">{ch.email}</a></div>
            <div className="flex items-center justify-center gap-2"><MapPin className="w-4 h-4" /> New Friends Colony, New Delhi 110025</div>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <Button asChild className="bg-amber-500 hover:bg-amber-600 text-zafco-blue font-bold" data-testid="saudi-work-final-cta-apply">
              <Link to={`/contact${contactQuery}&intent=apply`}>Get Customised Quote <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button asChild variant="outline" className="bg-white text-zafco-blue border-white hover:bg-white/90" data-testid="saudi-work-final-cta-whatsapp">
              <a href={ch.whatsappUrlWith(waMessage)} target="_blank" rel="noopener noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp {ch.whatsappDisplay}</a>
            </Button>
            <Button asChild variant="outline" className="bg-transparent text-white border-white hover:bg-white/10" data-testid="saudi-work-final-cta-call">
              <a href={ch.phoneTelUrl}><Phone className="w-4 h-4 mr-2" /> Call {ch.phoneDisplay}</a>
            </Button>
          </div>

          <div className="rounded-xl bg-white/5 border border-white/10 p-5 text-left text-sm text-white/80 max-w-3xl mx-auto">
            <p className="font-bold text-white mb-2">ZAFCO Pan-India Centres</p>
            <div className="grid md:grid-cols-2 gap-1.5">
              {CITY_CENTERS.slice(0, 6).map((c) => (
                <div key={c[0]} className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-amber-300 mt-0.5 flex-shrink-0" /> <span>{c[0]} — {c[1]}</span></div>
              ))}
            </div>
          </div>

          <p className="text-xs text-white/60 mt-8">
            ZAFCO Human Resource Management · MEA Registration: {MEA_REG} · Authorised Agency by the Royal Embassy of Saudi Arabia · Government of India Approved · 15+ Years Experience · 98% Success Rate · 400,000+ Visas Processed
          </p>
        </div>
      </section>
    </div>
  );
};

export default SaudiWorkVisaPage;
